import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import brokerIntegrationRepository from '../repositories/brokerIntegrationRepository';
import { mapBrokerIntegrations } from '../services/brokerIntegrationMapper';
import brokerAccountService from '../services/brokerAccountService';
import { useNotifications } from '../contexts/NotificationContext';
import { manualDeleteEngagements, deleteEngagementByUsername } from '../utils/manualDeleteEngagements';
import { deleteEngagementsForUser } from '../utils/engagementCleanup';
import { extractAccountId } from '../utils/accountDataStandardizer';
import { coordinatedBrokerCleanup, coordinatedEngagementCleanup } from '../utils/cleanupCoordinator';
import { createDebouncedFirestoreSubscription, removeDebouncedSubscription } from '../utils/subscriptionDebouncer';

const BrokerConnectionsContext = createContext({});

export const useBrokerConnections = () => {
  const context = useContext(BrokerConnectionsContext);
  if (!context) {
    throw new Error('useBrokerConnections must be used within BrokerConnectionsProvider');
  }
  return context;
};

/**
 * BrokerConnectionsProvider
 *
 * Responsibilities:
 * - Subscribe to Firestore broker integration changes
 * - Transform data through mapper
 * - Expose clean API for broker connections
 * - Manage account balance fetching (delegating to backend)
 *
 * NOT responsible for:
 * - Token management (handled by backend scheduledTokenRefresh)
 * - Direct Firestore writes (use repository/service)
 * - Notification side effects (use dedicated service)
 */
export const BrokerConnectionsProvider = ({ children }) => {
  const { user, userProfile } = useAuthContext();
  const { notify } = useNotifications();

  // Core state
  const [connections, setConnections] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Ref to prevent infinite re-render loops in callbacks
  const connectionsRef = useRef([]);

  // Balance cache with TTL
  const [balanceCache] = useState(() => new Map());
  const BALANCE_CACHE_TTL = 2 * 60 * 1000; // 2 minutes

  // Subscribe to Firestore changes with debouncing
  useEffect(() => {
    if (!user?.uid) {
      setConnections([]);
      setSelectedAccounts([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    console.log('🔗 Setting up debounced Firestore subscription for user:', user.uid);

    // Create debounced handler for broker integration updates
    const subscriptionKey = `broker_integrations_${user.uid}`;
    const debouncedHandler = createDebouncedFirestoreSubscription(
      subscriptionKey,
      (snapshot) => {
        try {
          console.log('📥 Debounced Firestore update received, docs:', snapshot.length);

          // Transform raw Firestore data to view models
          const { connections: mappedConnections, selectedAccounts: mappedAccounts } =
            mapBrokerIntegrations(snapshot);

          // Check if credentials were updated (clear balance cache)
          const shouldClearCache = connectionsRef.current.some(oldConn => {
            const newConn = mappedConnections.find(c => c.id === oldConn.id);
            return newConn && (
              newConn.credentials?.updatedAt !== oldConn.credentials?.updatedAt ||
              newConn.metadata?.lastAccountSync !== oldConn.metadata?.lastAccountSync
            );
          });

          if (shouldClearCache) {
            console.log('🧹 Clearing balance cache due to credential updates');
            balanceCache.clear();
          }

          console.log('🔄 Updating connections state:', mappedConnections.length);
          setConnections(mappedConnections);
          setSelectedAccounts(mappedAccounts);
          setError(null);
        } catch (err) {
          console.error('Error processing broker integrations:', err);
          setError(err.message);
        } finally {
          setLoading(false);
        }
      },
      'BROKER_INTEGRATIONS'
    );

    // Set up Firestore subscription with debounced handler
    const unsubscribe = brokerIntegrationRepository.subscribeToUserIntegrations(
      user.uid,
      debouncedHandler,
      (err) => {
        console.error('Firestore subscription error:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => {
      console.log('🔌 Unsubscribing from debounced Firestore for user:', user?.uid);
      unsubscribe();
      removeDebouncedSubscription(subscriptionKey);
    };
  }, [user?.uid]);

  // Keep ref in sync to prevent infinite loops in callbacks
  useEffect(() => {
    connectionsRef.current = connections;
  }, [connections]);

  // Auto cleanup disconnected integrations on mount (only run once)
  const hasRunCleanupRef = useRef(false);

  useEffect(() => {
    if (!user?.uid || hasRunCleanupRef.current) return;

    hasRunCleanupRef.current = true;

    const performCleanup = async () => {
      try {
        console.log('🧹 BrokerConnectionsProvider: Starting coordinated auto cleanup...');
        const result = await coordinatedBrokerCleanup({
          description: 'Auto cleanup of disconnected integrations on mount'
        });
        if (result.success && result.deleted > 0) {
          console.log(`✅ Coordinated auto cleanup completed: ${result.message}`);
        }
      } catch (error) {
        console.error('❌ Coordinated auto cleanup failed:', error);
        // Fallback to direct cleanup if coordinator fails
        try {
          console.log('🔄 Falling back to direct cleanup...');
          const fallbackResult = await brokerIntegrationRepository.cleanupDisconnectedIntegrations();
          if (fallbackResult.success && fallbackResult.deleted > 0) {
            console.log(`✅ Fallback cleanup completed: ${fallbackResult.message}`);
          }
        } catch (fallbackError) {
          console.error('❌ Fallback cleanup also failed:', fallbackError);
        }
      }
    };

    // Run cleanup after a short delay to avoid interfering with initial load
    const cleanupTimer = setTimeout(performCleanup, 2000);

    return () => clearTimeout(cleanupTimer);
  }, [user?.uid]);

  // Fetch account balances with token refresh handling
  const fetchAccountBalances = useCallback(async (integrationId, accountId, forceRefresh = false, retryCount = 0) => {
    if (!user?.uid) return null;

    const cacheKey = `${integrationId}_${accountId}`;
    const maxRetries = 2;

    // Check cache first
    if (!forceRefresh && balanceCache.has(cacheKey)) {
      const cached = balanceCache.get(cacheKey);
      if (Date.now() - cached.timestamp < BALANCE_CACHE_TTL) {
        return cached.data;
      }
    }

    try {
      // Find the integration to get broker type (use ref to prevent infinite loops)
      const integration = connectionsRef.current.find(c => c.id === integrationId);
      if (!integration) {
        console.warn('Integration not found for balance fetch:', integrationId);
        return null;
      }

      // Check if token needs refresh before making API call
      const tokenExpiry = integration?.credentials?.expiresAt;
      const isTokenExpired = tokenExpiry ? new Date(tokenExpiry) <= new Date() : false;
      const isTokenExpiringSoon = tokenExpiry ? new Date(tokenExpiry) <= new Date(Date.now() + 5 * 60 * 1000) : false; // 5 minutes

      if (isTokenExpired || isTokenExpiringSoon) {
        console.log('🔄 Token expired or expiring soon, triggering refresh before balance fetch');
        try {
          await triggerTokenRefresh(integrationId, integration.broker);
          // Wait a moment for the refresh to complete and state to update
          await new Promise(resolve => setTimeout(resolve, 1000));
        } catch (refreshError) {
          console.error('❌ Token refresh failed before balance fetch:', refreshError);
          if (retryCount >= maxRetries) {
            return null;
          }
        }
      }

      // Resolve accountId using standardized extraction
      const resolvedAccountId = accountId ||
        extractAccountId(integration?.credentials) ||
        extractAccountId(integration?.metadata?.selectedAccount) ||
        extractAccountId(integration?.metadata) ||
        null;

      console.log('🔎 Provider.fetchAccountBalances params:', {
        integrationId,
        broker: integration.broker,
        userId: user.uid,
        accountId: resolvedAccountId,
        retryCount
      });

      if (!resolvedAccountId) {
        console.warn('⚠️ Missing accountId in provider. Skipping balance request.');
        return null;
      }

      // Backend will use fresh token from scheduledTokenRefresh
      const response = await brokerAccountService.fetchAccountBalances({
        broker: integration.broker,
        userId: user.uid,
        accountId: resolvedAccountId
      });

      console.log('📦 Provider received response:', {
        hasSuccess: !!response?.success,
        hasData: !!response?.data,
        hasBalances: !!response?.balances,
        responseKeys: response ? Object.keys(response) : [],
        retryCount
      });

      // Backend now returns { success: true, data: StandardizedBalanceData }
      if (response?.success && response?.data) {
        // Cache the standardized balance data
        balanceCache.set(cacheKey, {
          data: response.data,
          timestamp: Date.now()
        });
        return response.data;
      }

      return null;
    } catch (error) {
      console.error('Error fetching account balances:', error);

      // Check if error is token-related and we haven't exceeded retries
      const isTokenError = error.message?.includes('token') ||
                          error.message?.includes('401') ||
                          error.message?.includes('unauthorized') ||
                          error.message?.includes('expired');

      if (isTokenError && retryCount < maxRetries) {
        console.log(`🔄 Token error detected, retrying balance fetch (attempt ${retryCount + 1}/${maxRetries})`);
        try {
          const integration = connectionsRef.current.find(c => c.id === integrationId);
          if (integration) {
            await triggerTokenRefresh(integrationId, integration.broker);
            // Wait for refresh and retry
            await new Promise(resolve => setTimeout(resolve, 2000));
            return fetchAccountBalances(integrationId, accountId, true, retryCount + 1);
          }
        } catch (refreshError) {
          console.error('❌ Token refresh failed during retry:', refreshError);
        }
      }

      return null;
    }
  }, [user?.uid]);

  // Trigger token refresh for a specific integration
  const triggerTokenRefresh = useCallback(async (integrationId, broker) => {
    if (!user?.uid) {
      throw new Error('User must be authenticated');
    }

    console.log('🔄 Triggering token refresh for:', { integrationId, broker, userId: user.uid });

    try {
      // Import the service to avoid circular dependencies
      const response = await fetch(`/api/auth/${broker}/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await user.getIdToken()}`
        },
        body: JSON.stringify({
          user_id: user.uid,
          integration_id: integrationId
        })
      });

      if (!response.ok) {
        const error = await response.text();
        throw new Error(`Token refresh failed: ${error}`);
      }

      const result = await response.json();
      console.log('✅ Token refresh successful:', result);

      // Clear balance cache for this integration since tokens changed
      for (const [key] of balanceCache) {
        if (key.startsWith(`${integrationId}_`)) {
          balanceCache.delete(key);
        }
      }

      return result;
    } catch (error) {
      console.error('❌ Error triggering token refresh:', error);
      throw error;
    }
  }, [user, balanceCache]);

  // Add new broker connection
  const connectBroker = useCallback(async (broker, credentials) => {
    if (!user?.uid) {
      throw new Error('User must be authenticated');
    }

    try {
      const integrationId = await brokerIntegrationRepository.createIntegration(
        user.uid,
        broker,
        credentials,
        userProfile
      );

      // Notify user
      if (notify?.brokerConnected) {
        const brokerDisplayName = broker.charAt(0).toUpperCase() +
          broker.slice(1).replace('_', ' ');
        await notify.brokerConnected(brokerDisplayName);
      }

      // Fetch initial balance - try to get accountId from multiple sources
      let accountIdForBalance = credentials.accountId;

      // If no accountId in credentials, try to get from accounts array
      if (!accountIdForBalance && brokerIntegration?.accounts?.length > 0) {
        accountIdForBalance = brokerIntegration.accounts[0]?.account_number ||
                             brokerIntegration.accounts[0]?.accountNumber ||
                             brokerIntegration.accounts[0]?.['account-number'] ||
                             null;
        console.log('🔍 Using first available account for initial balance fetch:', accountIdForBalance);
      }

      if (accountIdForBalance) {
        setTimeout(() => {
          fetchAccountBalances(integrationId, accountIdForBalance, true);
        }, 500);
      } else {
        console.warn('⚠️ No account ID available for initial balance fetch');
      }

      return integrationId;
    } catch (error) {
      console.error('Error connecting broker:', error);
      throw error;
    }
  }, [user?.uid, userProfile, notify, fetchAccountBalances]);

  // Disconnect broker
  const disconnectBroker = useCallback(async (integrationId) => {
    console.log('🔍 BrokerConnectionsProvider.disconnectBroker called with integrationId:', integrationId);
    console.log('🔍 Current user.uid:', user?.uid);

    if (!user?.uid) {
      throw new Error('User must be authenticated');
    }

    try {
      const integration = connectionsRef.current.find(c => c.id === integrationId);
      console.log('🔍 Found integration:', integration);
      if (!integration) {
        throw new Error('Integration not found');
      }

      console.log('🔍 Deleting broker integration from Firestore...');
      await brokerIntegrationRepository.deleteIntegration(user.uid, integrationId);
      console.log('✅ Broker integration deleted from Firestore');

      // Delete all engagement data using coordinated cleanup
      console.log('🗑️ Starting coordinated engagement cleanup for user:', user.uid);
      try {
        const deleted = await coordinatedEngagementCleanup(user.uid, userProfile, {
          description: `Engagement cleanup for broker disconnection: ${integration.broker}`
        });
        console.log(`✅ Coordinated engagement cleanup complete. Deleted ${deleted} documents.`);
      } catch (engagementError) {
        console.error('❌ Error during coordinated engagement cleanup:', engagementError);
        // Fallback to direct cleanup
        try {
          console.log('🔄 Falling back to direct engagement cleanup...');
          const fallbackDeleted = await deleteEngagementsForUser({
            userId: user.uid,
            email: userProfile?.email,
            username: userProfile?.username
          });
          console.log(`✅ Fallback engagement cleanup complete. Deleted ${fallbackDeleted} documents.`);
        } catch (fallbackError) {
          console.error('❌ Fallback engagement cleanup also failed:', fallbackError);
        }
      }

      // Clear balance cache for this integration
      for (const [key] of balanceCache) {
        if (key.startsWith(`${integrationId}_`)) {
          balanceCache.delete(key);
        }
      }

      // Notify user
      if (notify?.brokerDisconnected) {
        const brokerDisplayName = integration.broker.charAt(0).toUpperCase() +
          integration.broker.slice(1).replace('_', ' ');
        await notify.brokerDisconnected(brokerDisplayName);
      }

      return true;
    } catch (error) {
      console.error('Error disconnecting broker:', error);
      throw error;
    }
  }, [user?.uid, userProfile, notify, balanceCache]);

  // Get connection by broker
  const getConnectionByBroker = useCallback((broker) => {
    return connectionsRef.current.find(c =>
      c.broker === broker &&
      (c.status === 'active' || c.status === 'connected')
    );
  }, []);


  // Compute derived status
  const connectionStatus = useMemo(() => {
    const activeConnections = connections.filter(c => {
      return c.status === 'active' || c.status === 'connected';
    });

    const expiredConnections = connections.filter(c => {
      return c.status === 'expired' || c.status === 'disconnected' || c.status === 'needs_reauth';
    });

    return {
      connections,
      activeConnections,
      expiredConnections,
      selectedAccounts,
      hasConnections: activeConnections.length > 0,
      hasExpiredConnections: expiredConnections.length > 0,
      hasAnyConnections: connections.length > 0,
      connectionCount: activeConnections.length
    };
  }, [connections, selectedAccounts]);

  // Public API
  const value = useMemo(() => ({
    // State
    ...connectionStatus,
    loading,
    error,

    // Actions
    connectBroker,
    disconnectBroker,
    fetchAccountBalances,
    getConnectionByBroker,
    triggerTokenRefresh,

    // Supported brokers
    SUPPORTED_BROKERS: {
      TRADIER: 'tradier',
      TASTYTRADE: 'tastytrade',
      SCHWAB: 'schwab',
      INTERACTIVE_BROKERS: 'interactive_brokers'
    }
  }), [connectionStatus, loading, error, connectBroker, disconnectBroker, fetchAccountBalances,
      getConnectionByBroker, triggerTokenRefresh]);

  return (
    <BrokerConnectionsContext.Provider value={value}>
      {children}
    </BrokerConnectionsContext.Provider>
  );
};